/* eslint-disable object-curly-newline */
import { defineStore } from "pinia";
import { sDs } from '@common/sDs/sDs';
import { library } from "@common/library/library";
import { configStore } from "./configStore";

// The actual object where data is stored
// The pinia store will return a proxy of this object
// The proxy creates sub-records and sub-fields if they are not available in the store
// The proxy may be configured to request data from the server if the field does not exist (not currently enabled)
const handlerStore = {
    records: {
        vol: {},
        opp: {},
        org: {},
        sch: {},
        lbe: {},
        mem: {},
        aff: {},
        aaw: {},
        assignment: {},
        availabilityWindows: {},
        uiTagged: {},
        cstProperty: {},
        referrals: {}, // Relative to a vol
        placements: {}, // Relative to a vol
        assignments: {}, // Relative to a vol
        ae: {},
        msg: {},
        history: {},
        subscription: {}, // Holds individual record Proxies for each subscription
    },
    subscriptions: {}, // Holds details about each subscription including: key (ID), payload, updateFrequency, lastRequestTime
};
let initialHandlerStore = library.cloneObj(handlerStore);

// catch all nested properties and returns the proxy e.g. this.recordStore.vol || this.recordStore.vol[123] || this.recordStore.vol[123].VOL_FIRST_NAME
const isProxy = Symbol('isProxy'); // Unique key that cannot be overwritten without using this variable

const locals = {
    setRecords(iRecords, psp) {
        let recordKeys = Object.keys(iRecords);
        if (!recordKeys.length) return;
        recordKeys.forEach((recId) => {
            // eslint-disable-next-line no-use-before-define
            library.mergeObj(recordProxy[psp][recId], iRecords[recId]);
        });
    },
    updatePerformance(subscription, perfKey, thisPerf) {
        // all times in milliseconds
        let subPerf = subscription.performance;
        let bestPerf = subPerf[perfKey].best;
        let worstPerf = subPerf[perfKey].worst;
        if (!bestPerf || thisPerf < bestPerf) subPerf[perfKey].best = thisPerf;
        if (!worstPerf || thisPerf > worstPerf) subPerf[perfKey].worst = thisPerf;
        subPerf[perfKey].history.push(thisPerf);
    }
};

const proxyKeys = {
    lbeIds: 'relKeys',
    affIds: 'relKeys',
    assignmentIds: 'relKeys',
    placedIds: 'relKeys',
    referredIds: 'relKeys',
    historyIds: 'relKeys',
    schIds: 'relKeys',
    affiliations: 'rel',
    assignments: 'rel',
    placements: 'rel',
    referrals: 'rel',
    lbes: 'rel',
    shifts: 'rel',
    vol: 'psp',
    opp: 'psp',
    org: 'psp',
    sch: 'psp',
    acc: 'psp',
    mem: 'psp',
    lbe: 'psp',
    aff: 'psp',
    assignment: 'psp',
    ae: 'psp',
    history: 'psp',
    cstProperty: 'psp',
    subscription: 'psp',
    uiTagged: 'psp',
    aaw: 'psp',
    linked_opp: 'link',
    linked_vol: 'link',
    linked_org: 'link',
    rsLastModified: 'internal',
    inSubscription: 'internal',
    uiRecordPsp: 'internal',
    uiRecordId: 'internal',
    uiRecordKey: 'internal',
    records: 'internal',
    __v_isRef: 'ignore',
    __v_raw: 'ignore',
    __v_isReadonly: 'ignore',
    __v_skip: 'ignore',
    length: 'ignore',
    strict: 'strict'
};
const keyType = (key, psp) => {
    if (typeof key === 'symbol') return 'ignore';
    if (proxyKeys[key]) return proxyKeys[key];
    // undefined keys are either a record ID a field or a subscription
    if (!isNaN(key)) return 'id';
    return 'field';
};

const recordHandler = {
    get(obj, key, receiver) { // vol.VOL_FIRST_NAME
        let psp = obj.perspective;
        let strict = obj.isStrict;
        let kType = keyType(key, psp);
        if (kType === 'ignore') return Reflect.get(...arguments); // Don't create proxies on symbols
        if ((kType === 'psp' || kType === 'id' || kType === 'rel' || kType === 'link' || kType === 'strict') && !obj.isRelId && (!obj[key] || !obj[key][isProxy])) {
            if (!obj[key]) obj[key] = {};
            // set the perspective for access in other handler conditions
            let perspective = kType === 'psp' ? key : psp;
            let isStrict = kType === 'strict' ? 1 : strict;
            Object.defineProperty(obj[key], 'perspective', {
                enumerable: false,
                value: perspective
            });
            Object.defineProperty(obj[key], 'isStrict', {
                enumerable: false,
                value: isStrict
            });
            if (kType === 'id' && psp !== 'subscription' && psp !== 'uiTagged') {
                obj[key].uiRecordId = Number(key);
                obj[key].uiRecordPsp = psp;
            }
            obj[key][isProxy] = true;
            obj[key] = new Proxy(obj[key], recordHandler);
        }
        if (psp === 'subscription') {
            if (key === 'rsLastModified') {
                let subscription = handlerStore.subscriptions[obj.subscriptionKey];
                if (subscription && subscription.sourceType === 'uiTag') {
                    if (handlerStore.records.uiTagged[subscription.params.tagName]) {
                        obj.records = handlerStore.records.uiTagged[subscription.params.tagName].records;
                        obj.rowCount = handlerStore.records.uiTagged[subscription.params.tagName].rowCount;
                        // rsLastModified for subscribers of this tagged data is set in getSubscription getter
                    }
                } else if (subscription && subscription.psp === 'cstProperty') {
                    // TODO figure out a better way to make sure this object/subkey exists
                    let subscription = handlerStore.subscriptions[obj.subscriptionKey];
                    if (subscription.params && subscription.params.cstProperty) {
                        if (handlerStore.records.cstProperty[subscription.params.cstProperty]
                            && handlerStore.records.cstProperty[subscription.params.cstProperty][subscription.params.subKey]) {
                            obj.records = handlerStore.records.cstProperty[subscription.params.cstProperty][subscription.params.subKey];
                        } else {
                            obj.records = {};
                        }
                    }
                } else {
                    if (subscription && subscription.storeOnlyReturnedRecords) obj.records = [];
                    let subIdsLen = subscription && subscription.recordIds && subscription.recordIds.length;
                    if (subIdsLen) {
                        for (let i = 0; i < subIdsLen; i++) {
                            let recordId = subscription.recordIds[i];
                            if (recordId) {
                                if (!handlerStore.records[subscription.psp][recordId]?.inSubscription) {
                                    handlerStore.records[subscription.psp][recordId].inSubscription = {};
                                }
                                handlerStore.records[subscription.psp][recordId].inSubscription[obj.subscriptionKey] = true;
                                obj.records[recordId] = handlerStore.records[subscription.psp][recordId];
                            }
                        }
                    }
                }
            }
            else if (kType === 'id') {
                if (!obj[key].rsLastModified) obj[key].rsLastModified = false;
                if (!obj[key].records) obj[key].records = {};
                Object.defineProperty(obj[key], 'subscriptionKey', {
                    enumerable: false,
                    value: key
                });
                let subscription = handlerStore.subscriptions[key];
                if (subscription && subscription.source === 'uiTag'
                    && subscription.payload
                    && subscription.payload.params
                    && subscription.payload.params.tagName
                ) {
                    Object.defineProperty(obj[key], 'uiTag', {
                        enumerable: false,
                        value: subscription.payload.params.tagName
                    });
                }
            }
        }
        // process relKeys properties
        if (kType === 'relKeys' && (!obj[key] || !obj[key][isProxy])) {
            if (!obj[key]) obj[key] = {};
            // use isRelId for the next level up
            Object.defineProperty(obj[key], 'isRelId', {
                enumerable: false,
                value: true
            });
            obj[key][isProxy] = true;
            obj[key] = new Proxy(obj[key], recordHandler);
        }
        // set relative keys to false by default
        if (obj.isRelId && typeof obj[key] === 'undefined') obj[key] = false;
        // Convert lbeIds to lbes (keys to records)
        if (kType === 'rel' && obj.uiRecordId) {
            let convertedKeys = {
                lbes: { relPsp: 'lbe', idsKey: 'lbeIds' },
                affiliations: { relPsp: 'aff', idsKey: 'affIds' },
                shifts: { relPsp: 'sch', idsKey: 'schIds' },
                referrals: { relPsp: 'referrals', idsKey: 'referredIds' },
                placements: { relPsp: 'placements', idsKey: 'placedIds' }
            };
            let relPsp = convertedKeys[key].relPsp;
            let idsKey = convertedKeys[key].idsKey; // lbeIds, schIds, affIds
            let recordKeys = [];
            let relIds = handlerStore.records[psp][obj.uiRecordId][idsKey];
            if (relIds) recordKeys = Object.keys(relIds);
            let relRecords = {};
            recordKeys.forEach((iKey) => {
                relRecords[iKey] = handlerStore.records[relPsp][iKey];
            });
            obj[key] = relRecords;
        }
        if (kType === 'link') {
            let linkPsp = key.split('_')[1];
            let linkedRecordId = obj[key].uiRecordId;
            let record = obj[key]; // default
            if (linkedRecordId) record = handlerStore.records[linkPsp][linkedRecordId]; // relative
            // remove all relative records in linked records to prevent circular dependency
            let { lbeIds, affIds, schIds, lbes, affiliations, shifts, ...otherKeys } = record;
            obj[key] = otherKeys;
        }
        // { VOL_FIRST_NAME: 'bob' } 'VOL_FIRST_NAME'
        let tmpValue = obj[key];
        if (kType === 'field' && key !== 'uiRecordId' && key !== 'toJSON' && key !== 'ID' && key !== 'uiDelete' && key !== 'doNotAutosubscribe') {
            // if Field and no subscription for the record, and the value is undefined, subscribe to get the field
            // eslint-disable-next-line no-use-before-define
            let rStore = typeof recordStore === 'function' && recordStore();
            let recordId = receiver.uiRecordId;
            if (rStore && recordId) {
                if (typeof obj[key] === 'undefined' && !obj.doNotAutosubscribe) {
                    let fields = [key];
                    obj[key] = null;
                    let source = 'er_' + psp + 'Fields';
                    if (source === 'er_affFields') source = 'sds_getProfileField';
                    rStore.subscribe({ source: [source], params: { recordId, fields, recordPsp: psp } }, 'autosubscriber');
                }
            }
            if (!obj.isStrict) {
                // isStrict = return data as returned from server, without flag parse data from server
                // @todo isStrict should be equal with strictTypeResults and recordStore should keep both variants of data
                if (typeof tmpValue === 'string' && tmpValue.indexOf('datePeriod') !== -1 && library.isJson(tmpValue)) {
                    let dateJson = JSON.parse(tmpValue);
                    tmpValue = library.getDateBaseOnPeriod(dateJson.datePeriod, dateJson.amount, false, dateJson.unit, dateJson.day);
                }
            }
        }
        return tmpValue; // bob
    },
    set(obj, key, value) {
        if (key === 'rsLastModified') {
            let subscription = handlerStore.subscriptions[obj.subscriptionKey];
            let subTag = subscription && subscription.tagName;
            if (subTag) {
                if (value === -1) {
                    delete handlerStore.records.uiTagged[subTag];
                } else {
                    let subIdsLen = subscription && subscription.recordIds && subscription.recordIds.length;
                    let rowCount = 0;
                    let uiTaggedRecords = {};
                    // Process tagLogic when records change
                    // Note: tagLogic is also processed when store properties change, handled in the getSubscription getter
                    for (let i = 0; i < subIdsLen; i++) {
                        let recordId = subscription.recordIds[i];
                        if (library.logicParser.call(subscription.tagParent, subscription.tagLogic, obj.records[recordId])) {
                            uiTaggedRecords[recordId] = obj.records[recordId];
                            rowCount++;
                        }
                    }
                    // handle tagging here
                    if (!handlerStore.records.uiTagged[subTag]) {
                        handlerStore.records.uiTagged[subTag] = {};
                    }
                    handlerStore.records.uiTagged[subTag].records = uiTaggedRecords;
                    handlerStore.records.uiTagged[subTag].rsLastModified = value;
                    handlerStore.records.uiTagged[subTag].rowCount = rowCount;
                }
            }
        }
        obj[key] = value;
        return true;
    }
};
const recordProxy = new Proxy(handlerStore.records, recordHandler);

const recordStore = defineStore('recordStore', {
    state: () => ({
        records: recordProxy,
        requests: {},
        subscriptions: handlerStore.subscriptions,
        pollingInterval: false, // seconds
        subscriptionCheckFrequency: 10, // seconds
        timeSinceLastRefresh: 0,
        refreshInterval: false,
        excludeActionsFromFreezeDetection: ['startSubscriberPolling']
    }),
    getters: {
        getSubscription() {
            return (subscription) => {
                let subscriptionId = typeof subscription === 'object'
                    ? subscription.key
                    : subscription;
                // checking changes to rsLastModified triggers updates to the subscription
                let rsLastModified = this.records.subscription[subscriptionId] && this.records.subscription[subscriptionId].rsLastModified;
                if (!rsLastModified) return {}; // empty records
                // Process logic that looks at layout stores here
                if (typeof subscription === 'object' && subscription.tagName) {
                    if (Array.isArray(subscription.payload.tagLogic)) {
                        subscription.payload.tagLogic.forEach((logicItem) => {
                            let rules = logicItem.criteria && logicItem.criteria.rules;
                            let hasStorePropRule = false;
                            if (Array.isArray(logicItem.criteria.rules)) {
                                rules.forEach((rule) => {
                                    if (rule.field.indexOf('Store Prop:') === 0 || (typeof rule.value === 'string' && rule.value.indexOf('Store Prop:') !== -1)) {
                                        // TODO store anc compare existing/new values ?
                                        hasStorePropRule = true;
                                    }
                                });
                            }
                            if (hasStorePropRule) {
                                rsLastModified = new Date().getTime();
                                this.records.subscription[subscriptionId].rsLastModified = rsLastModified;
                            }
                        });
                    }
                    // if this subscription is a uiTag subscription, Update all subscribers of the uiTagged data
                    for (let sKey in this.records.subscription) {
                        if (sKey === 'length') continue;
                        if (this.records.subscription[sKey].uiTag === subscription.tagName) {
                            this.records.subscription[sKey].rsLastModified = rsLastModified;
                        }
                    }
                }
                return this.records.subscription[subscriptionId].records;
            };
        }
    },
    actions: {
        reset() {
            let tmpHandlerStore = library.cloneObj(initialHandlerStore);
            this.records = new Proxy(tmpHandlerStore.records, recordHandler);
            this.requests = {};
            this.subscriptions = tmpHandlerStore.subscriptions;
            this.subscriptionCheckFrequency = 10;
            this.timeSinceLastRefresh = 0;
            if (this.pollingInterval) clearInterval(this.pollingInterval);
            if (this.refreshInterval) clearInterval(this.refreshInterval);
            this.pollingInterval = false;
            this.refreshInterval = false;
        },
        // Start the subscription watch interval
        startSubscriberPolling() {
            if (!this.refreshInterval) {
                this.refreshInterval = setInterval(() => {
                    this.timeSinceLastRefresh++;
                }, 1000); // NOSONAR
            }
            if (!this.pollingInterval) {
                this.pollingInterval = setInterval(() => {
                    // Evaluates the request's updateFrequency and lastRequestTime time to determine if a request should be sent
                    // uncomment below to view store data
                    // DO NOT LEAVE THESE LOGS ACTIVE IN COMMITTED CODE
                    // console.log(this.subscriptions); /* @phpstan-ignore-line */
                    // console.log(this.records);       /* @phpstan-ignore-line */
                    this.renewSubscriptions();
                }, this.subscriptionCheckFrequency * 1000); // NOSONAR
            }
        },
        findExistingSubscription(payload, existingKey, subscriber) {
            // Find using key
            if (existingKey && this.subscriptions[existingKey]) {
                if (subscriber) this.subscriptions[existingKey].subscribers[subscriber] = 'subscribed';
                return this.subscriptions[existingKey];
            }
            // Find using payload or single existing recordId
            let existingSub = false;
            let recordId = payload.params && (payload.params.recordId || payload.params.volId);
            let source = Array.isArray(payload.source) ? payload.source[0] : payload.source;
            let subsKey = Object.keys(this.subscriptions);
            for (let i = 0; i < subsKey.length; i++) {
                let iSub = this.subscriptions[subsKey[i]];
                let iPayload = iSub.payload;
                let iParams = iPayload && iPayload.params;
                // Find existing subscription with single recordId (fields request only eg: er_volFields)
                // and update the list of fields for the subscription
                let fields = payload.params && payload.params.fields;
                let iRecordId = iParams && iParams.recordId;
                let iFields = iParams && iParams.fields;
                let iPsp = iParams?.recordPsp || iParams?.psp;
                iFields = library.cloneObj(iFields);
                let allowedAddRecord = ['er_volFields', 'er_oppFields', 'er_orgFields', 'er_memFields', 'er_slotFields', 'er_schFields', 'er_userFields', 'er_accFields', 'er_affFields', 'sds_getProfileField'];
                let sameRecordId = Array.isArray(iRecordId) ? iRecordId.indexOf(recordId) !== -1 : recordId === iRecordId;
                if (payload.doNotAddRecords) {
                    allowedAddRecord = [];
                    if (sameRecordId && Array.isArray(iRecordId) && iRecordId.length > 1) sameRecordId = false;
                }
                let samePsp = iPsp === payload.params?.recordPsp;
                if (iSub.source === source && recordId && samePsp && (sameRecordId || allowedAddRecord.indexOf(source) !== -1) && fields && fields.length) {
                    iSub.existing = true;
                    let fieldsAdded = false;
                    let recordAdded = false;
                    if ((!fields || !iFields) && fields !== iFields) {
                        iFields = fields;
                        fieldsAdded = true;
                    }
                    else if (iFields) {
                        fields.forEach((field) => {
                            if (iFields.indexOf(field) === -1) { // NOSONAR
                                iFields.push(field);
                                fieldsAdded = true;
                            }
                        });
                    }
                    if (!Array.isArray(iRecordId)) iRecordId = [iRecordId];
                    let tmpRecordId = Array.isArray(recordId) ? recordId[0] : recordId;
                    if (iRecordId.indexOf(tmpRecordId) === -1) {
                        recordAdded = true;
                        iRecordId.push(tmpRecordId);
                    }
                    if (fieldsAdded || recordAdded) { // NOSONAR
                        iSub.existing = false;
                        iParams.fields = iFields;
                        iParams.recordId = iRecordId;
                    }
                    if (allowedAddRecord.indexOf(source) !== -1) {
                        // collect fields by record
                        if (typeof iSub.recordFields === 'undefined') iSub.recordFields = {};
                        let tmpRecordIds = Array.isArray(recordId) ? recordId : [recordId]; // Sometimes recordId is an array
                        for (let ii in tmpRecordIds) {
                            if (ii === 'length') continue;
                            let tmpId = tmpRecordIds[ii];
                            if (!Array.isArray(iSub.recordFields[tmpId])) iSub.recordFields[tmpId] = [];
                            for (let i in iFields) {
                                if (iSub.recordFields[tmpId].indexOf(iFields[i]) === -1) iSub.recordFields[tmpId].push(iFields[i]);
                            }
                            iSub.recordFields[tmpId].sort();
                        }
                    }
                    existingSub = iSub;
                    break;
                }
                // Find existing request by payload
                else if (iPayload && library.isEqualObj(payload, iPayload)) {
                    iSub.existing = true;
                    existingSub = iSub;
                    break;
                }
            }
            if (existingSub && subscriber) existingSub.subscribers[subscriber] = 'subscribed';
            return existingSub;
        },
        getSubscriptionDetails(payload, existingKey, subscriber, tagParent) {
            // Find existing subscription by comparing source and params to existing
            // Returns existing or new subscription
            let existingSub = this.findExistingSubscription(payload, existingKey, subscriber);
            // Put the new request in the subscriptions for replaying the request on interval
            if (!existingSub) {
                // Define Variables
                let { source, params, parentRecords, addParams, tagName, tagLogic, updateFrequency } = payload;
                if (Array.isArray(source)) source = source[0];
                if (!source) return false;
                let isLogbook = false;
                let psp;
                let sourceType;
                // set psp and isLogbook
                let sParts = source.split('___');
                if (source === 'uiTag') {
                    sourceType = 'uiTag';
                }
                else if (sParts.length === 3) { // NOSONAR if is dataset name
                    isLogbook = sParts[1];
                    if (isLogbook === '1') psp = 'lbe';
                    else psp = configStore.pspParentNamesAbbrev[sParts[0]];
                    sourceType = 'dataset';
                } else if (source.indexOf('Affiliation') !== -1) { // if is sDs call and not a fields request
                    psp = 'aff';
                    sourceType = 'sds';
                } else if (source === 'sds_getAffAvailabilityWindows') { // if is sDs call and not a fields request
                    psp = 'aaw';
                    sourceType = 'sds';
                } else if (source === 'er_messageList') { // Recruiter Requets
                    psp = 'msg';
                    sourceType = 'sds';
                } else if (source.indexOf('sds_getProfileField') === 0) { // if is sDs call and not a fields request
                    psp = params.recordPsp || 'ae';
                    sourceType = 'sdsFields';
                } else if (source.indexOf('ec_getHistory') === 0) { // if is sDs call and not a fields request
                    psp = 'history';
                    sourceType = 'sds';
                } else if (source.indexOf('ec_getGrid') === 0) { // if is sDs call and not a fields request
                    psp = params.gridPsp;
                    sourceType = 'sds';
                } else if (source.indexOf('getCstProperty') !== -1) {
                    psp = 'cstProperty';
                    sourceType = 'sds';
                } else if (source.indexOf('er_getPlacedSlotList') !== -1 || source.indexOf('er_getReferredSlotList') !== -1) {
                    psp = 'sch';
                    sourceType = 'sds';
                } else if (source.indexOf('er_getReferredOppList') !== -1 || source.indexOf('er_getPlacedOppList') !== -1) {
                    psp = 'opp';
                    sourceType = 'sds';
                } else if (source.indexOf('sds_get') === 0) { // if is sDs call and not a fields request
                    psp = source.split('sds_get')[1].substring(0, 3).toLowerCase(); // NOSONAR psp for sds_getVolAffiliations is vol
                    if (psp === 'aes') psp = 'sch'; // matches sds_getAeSlotsList
                    if (psp === 'ass') psp = 'assignment'; // matches sds_getAssignmentList
                    sourceType = 'sds';
                } else if (source.indexOf('configStore') === 0) {
                    sourceType = 'configStore';
                } else { // if it is a fields sDs call
                    sourceType = 'fields';
                    let tmpMatches = /er_(.*)Field/g.exec(source);
                    if (typeof tmpMatches?.[1] !== 'undefined') {
                        psp = tmpMatches[1];
                    }
                }

                let sKey = library.getUniqueID('nn');
                // It returns records from the recordStore whenever recordIds are added or removed
                let subscribers = {};
                if (subscriber) subscribers[subscriber] = 'subscribed';
                // Create path to subscription details
                this.subscriptions[sKey] = {
                    key: sKey,
                    payload,
                    params, // from payload
                    parentRecords, // from payload
                    source, // from payload
                    addParams, // from payload
                    sourceType,
                    psp,
                    tagName,
                    tagLogic,
                    tagParent,
                    subscribers,
                    isLogbook,
                    recordIds: [], // Records are added in the subscribe action after server response (used in subscriptionHandler.set)
                    updateFrequency: typeof updateFrequency === 'number' ? updateFrequency : 60, // in seconds
                    lastRequestTime: false,
                    performance: {
                        serverResponseTime: {
                            history: [],
                            best: null,
                            worst: null
                        },
                        recordStorePopulationTime: {
                            history: [],
                            best: null,
                            worst: null
                        }
                    },
                    initialLoading: true,
                    loading: false
                };
                // TODO add updateFrequency Logic eg: { logic: "record.VOL_AGE < 18", frequency: 10 } // Sets frequency to 10 seconds
                existingSub = this.subscriptions[sKey];
            }
            return existingSub;
        },
        subscribe(payload, subscriber, isUpdateRequest, existingKey, tagParent, fromSubDelay, force) {
            // Takes in sDs request params and generate a new, or find existing subscription
            // Sends request
            // Returns subscription Object
            // Subscriptions include all details to rerun the request including:
            // Perspective (psp), isLogbook, sDs request parameters (params)
            // Also includes settings for updateFrequency, and last request timestamp
            // subscriber is control name, cId of component, or other systematic ID in the code
            // Start the subscriber polling
            this.startSubscriberPolling();

            function formatFieldsParams(fields) {
                return Object.values(fields)
                    .map((field) => field.split(':::')[1] || field)
                    .filter(Boolean);
            }

            // Process source and params
            let subscriptionDetails = this.getSubscriptionDetails(payload, existingKey, subscriber, tagParent);
            if (subscriptionDetails.existing && !isUpdateRequest && !fromSubDelay && !force) return subscriptionDetails;
            if (!fromSubDelay) {
                // template can have multiple recordStore.records.vol[volId].VOL_FIRST_NAME
                // we do not need to call sDs each time, we should wait until all fields will be populated to the subscription
                // following code move the subscribe function to the timeout
                clearTimeout(this.subscriptions[subscriptionDetails.key].subscribeTimeout);
                this.subscriptions[subscriptionDetails.key].subscribeTimeout = setTimeout(() => {
                    this.subscribe(subscriptionDetails.payload, subscriber, isUpdateRequest, existingKey, tagParent, true);
                }, 500);
                return subscriptionDetails;
            }
            if (typeof this?.subscriptions?.[subscriptionDetails.key]?.subscribeTimeout !== 'undefined') {
                delete this.subscriptions[subscriptionDetails.key].subscribeTimeout;
            }
            let { psp, isLogbook, source, params, parentRecords, addParams } = subscriptionDetails;
            // Tells the server the date to use for data cutoff
            let modifiedAfter = (isUpdateRequest && subscriptionDetails.lastRequestTime) || false;
            if (typeof this.subscriptions?.[subscriptionDetails.key] !== 'undefined') this.subscriptions[subscriptionDetails.key].lastRequestTime = Math.floor(Date.now() / 1000); // NOSONAR;
            // Send Request and process response
            let start = performance.now();
            let lastRequestTime = Math.floor(Date.now() / 1000); // NOSONAR;
            if (subscriptionDetails.sourceType === 'uiTag') {
                // Do nothing with uiTagged subs
            }
            else if (subscriptionDetails.sourceType === 'dataset') {
                // Prepare request params
                let requestOptions = {
                    sDsFunction: 'sds_getDataset',
                    subscriptionKey: subscriptionDetails.key
                };
                // TODO Merge addParams from payload
                let otherAddParams = { useUdfCode: true, idAsKey: true, resetKey: false }; // TODO: What is resetKey?
                addParams = { ...addParams, ...otherAddParams };
                if (isUpdateRequest) {
                    addParams.modifiedAfter = modifiedAfter;
                    if (parentRecords) addParams.currentSubscriptionRecords = subscriptionDetails.recordIds;
                }
                // Send the request
                if (!parentRecords) parentRecords = [];
                this.subscriptions[subscriptionDetails.key].loading = true;
                sDs.post(requestOptions, source, psp, isLogbook, parentRecords, addParams)
                    .then((response) => {
                        if (typeof this.subscriptions[subscriptionDetails.key] !== 'undefined') {
                            locals.updatePerformance(subscriptionDetails, 'serverResponseTime', performance.now() - start);
                            if (response && response.status === 'success' && response.data) {
                                if (response.data.rowCount) {
                                    this.records.subscription[subscriptionDetails.key].rowCount = response.data.rowCount;
                                }
                                if (response.data.summary) {
                                    this.records.subscription[subscriptionDetails.key].summary = response.data.summary;
                                }
                                this.addRecordsToSubscription(response.data.records, subscriptionDetails.key);
                            }
                            this.subscriptions[subscriptionDetails.key].initialLoading = false;
                            this.subscriptions[subscriptionDetails.key].loading = false;
                            this.subscriptions[subscriptionDetails.key].lastRequestTime = lastRequestTime;
                        }
                    });
            }
            else if (subscriptionDetails.sourceType === 'sds') {
                // Parse the object of params into an array of params based on order required by sDs
                // TODO replace this with more robust mapping (only works for sds_getAffiliationList
                let sdsOption = configStore.getsDsList().find((option) => option.value === source);
                // let sourceParamMap = {
                // sds_getAffiliationList
                //  volId: 0,
                //  orgId: 1,
                //  fields: 2,
                //  affType: 3,
                // ec_getGrid
                //  gridPsp: 0,
                //  gridIds: 1,
                //  gridFields: 2,
                // ec_getHistory
                //  recordId: 0,
                //  type: 1,
                //  filter: 2,
                // er_getCstProperty, ec_getCstProperty
                //  cstProperty: 0,
                //  fromDevDash: 1,
                //  useLoggedUserInstance: 2
                // };
                // TODO Note: This order might be different for different sDs calls
                // TODO Rework this formattedPayload method
                let formattedPayload = ["", "", []]; // These are default values
                let paramList = Object.keys(params);
                let requestParamsLen = 0;

                // Set default param values (We need to have all params filled up to the highest param sent)
                paramList.forEach((pKey) => {
                    if (pKey === 'source') return; // Source is handled separately
                    let paramIndex = sdsOption.params[pKey] && sdsOption.params[pKey].paramIndex;
                    if (typeof paramIndex === 'undefined' && psp === 'cstProperty' && pKey === 'cstProperty') {
                        paramIndex = 0;
                    }
                    if (typeof paramIndex !== 'undefined') {
                        if (requestParamsLen < paramIndex) {
                            // Set the length of payload array to highest params index
                            requestParamsLen = paramIndex;
                        }
                    }
                });
                if (modifiedAfter && sdsOption.addParamsIndex && sdsOption.addParamsIndex > requestParamsLen) {
                    requestParamsLen = sdsOption.addParamsIndex;
                }
                formattedPayload.length = requestParamsLen + 1; // Adjust for 0 index
                formattedPayload.fill(false);

                // Overwrite default params
                paramList.forEach((pKey) => {
                    if (pKey === 'source') return; // Source is handled separately
                    let paramIndex = sdsOption.params[pKey] && sdsOption.params[pKey].paramIndex;
                    if (typeof paramIndex === 'undefined' && psp === 'cstProperty' && pKey === 'cstProperty') {
                        paramIndex = 0;
                    }
                    if (typeof paramIndex !== 'undefined') {
                        if (typeof params[pKey] !== 'undefined') {
                            // Overwrite default values
                            if (sdsOption.params[pKey]?.isFieldsParam) formattedPayload[paramIndex] = formatFieldsParams(params[pKey]);
                            else formattedPayload[paramIndex] = params[pKey];
                        }
                    }
                });

                let requestOptions = {
                    sDsFunction: source,
                    subscriptionKey: subscriptionDetails.key
                };

                // TODO make this more robust, allow it to be configured from the sources options
                let fieldMap = (field) => (field.split(':::').length === 2 ? field.split(':::')[1] : field);

                if (source === 'ec_getGrid' && Array.isArray(formattedPayload[2])) {
                    formattedPayload[2] = formattedPayload[2].map(fieldMap);
                    if (modifiedAfter) {
                        formattedPayload[6] = {
                            modifiedAfter: modifiedAfter,
                            currentSubscriptionRecords: subscriptionDetails.recordIds
                        };
                    }
                }
                if (source === 'sds_getAssignmentList' || source === 'sds_getAffiliationList') {
                    // Convert to psp based structure if not simple fields array
                    let fParamId = source === 'sds_getAssignmentList' ? 1 : 2;
                    if (Array.isArray(formattedPayload[fParamId]) && formattedPayload[fParamId].length && formattedPayload[fParamId][0].indexOf(':::') !== -1) {
                        let fieldsObj = {};
                        formattedPayload[fParamId].forEach((field) => {
                            let pspId = configStore.pspIds[field.split(':::')[0]];
                            let fieldName = field.split(':::')[1];
                            if (!fieldsObj[pspId]) fieldsObj[pspId] = [];
                            fieldsObj[pspId].push(fieldName);
                        });
                        formattedPayload[fParamId] = fieldsObj;
                    }
                }
                if (source === 'sds_getAeSlotsList') {
                    formattedPayload[2] = library.parseRelativeDate(formattedPayload[2]);
                    formattedPayload[3] = library.parseRelativeDate(formattedPayload[3]);
                }
                if (source === 'sds_getAeSlotsList' && Array.isArray(formattedPayload[1])) {
                    formattedPayload[1] = formattedPayload[1].map(fieldMap);
                    if (isUpdateRequest) {
                        formattedPayload[8] = {
                            modifiedAfter: modifiedAfter,
                            currentSubscriptionRecords: subscriptionDetails.recordIds
                        };
                    }
                }
                if (source === 'er_getPlacedSlotList' && isUpdateRequest) {
                    let volId = formattedPayload[5]; // NOSONAR
                    let currentSubscriptionRecords = {};
                    currentSubscriptionRecords[volId] = subscriptionDetails.recordIds;
                    formattedPayload[7] = { // NOSONAR
                        modifiedAfter: modifiedAfter,
                        currentSubscriptionRecords
                    };
                }
                this.subscriptions[subscriptionDetails.key].loading = true;
                sDs.post(requestOptions, ...formattedPayload).then((response) => {
                    locals.updatePerformance(subscriptionDetails, 'serverResponseTime', performance.now() - start);
                    if (response && response.status === 'success' && response.data) {
                        let data = response.data;
                        if (psp === 'cstProperty') {
                            if (!library.isJson(response.data) && typeof response.data?.[params.cstProperty] !== 'undefined') {
                                data = response.data[params.cstProperty];
                            }
                            data = JSON.parse(data);
                            if (params.subKey) data = data[params.subKey];
                        }
                        // Connect sub-records inside affiliation record
                        // TODO have each sub-record be connected to a recordStore proxy of the true record
                        if (source === 'sds_getAffiliationList' && data.ORGANIZATION) {
                            let orgData = library.cloneObj(data.ORGANIZATION);
                            delete data.ORGANIZATION;
                            for (let affId in data) {
                                if (affId === 'length') continue;
                                if (data[affId].AFF_RECORDS[3].length) {
                                    if (!data[affId].AFFILIATED_ORGS) data[affId].AFFILIATED_ORGS = {};
                                    // eslint-disable-next-line no-loop-func
                                    data[affId].AFF_RECORDS[3].forEach((aOrgId) => {
                                        data[affId].AFFILIATED_ORGS[aOrgId] = orgData[aOrgId];
                                    });
                                }
                                data[affId].DETAIL_RECORD = {};
                                let id1 = Array.isArray(data[affId].DETAIL_RECORD_ID) && data[affId].DETAIL_RECORD_ID[0];
                                if (id1) data[affId].DETAIL_RECORD = orgData[id1];
                            }
                        }
                        if (source === 'sds_getAssignmentList' && data.ASSIGNMENTS) {
                            let perspectives = Object.keys(data.RECORDS);
                            perspectives.forEach((pspId) => {
                                let pspAbbrev = configStore.pspParentNamesAbbrev[pspId];
                                if (pspAbbrev) {
                                    // Add records to but don't update subscription
                                    this.addRecordsToStore(data.RECORDS[pspId], [], pspAbbrev, subscriptionDetails);
                                }
                            });
                            data = data.ASSIGNMENTS;
                        }
                        this.addRecordsToSubscription(data, subscriptionDetails.key);
                    }
                    if (typeof this?.subscriptions?.[subscriptionDetails.key] !== 'undefined') {
                        this.subscriptions[subscriptionDetails.key].initialLoading = false;
                        this.subscriptions[subscriptionDetails.key].loading = false;
                        this.subscriptions[subscriptionDetails.key].lastRequestTime = lastRequestTime;
                    }
                });
            }
            else if (subscriptionDetails.sourceType === 'sdsFields') {
                // handle sDs calls to specific records
                let postRequest;
                let fields = params.fields;
                let recordId = params.recordId;
                let requestOptions = {
                    sDsFunction: 'sds_getProfileField',
                    subscriptionKey: subscriptionDetails.key
                };
                // eslint-disable-next-line no-case-declarations
                let tmpParams = { ...{ modifiedAfter: modifiedAfter }, ...params.params };
                if (psp && recordId && fields) {
                    this.subscriptions[subscriptionDetails.key].loading = true;
                    postRequest = sDs.post(requestOptions, psp, recordId, fields, tmpParams);
                    if (!postRequest) return false; // No Key is Produced
                    postRequest.then((response) => {
                        locals.updatePerformance(subscriptionDetails, 'serverResponseTime', performance.now() - start);
                        if (response && response.status === 'success' && response.data) {
                            this.addRecordsToSubscription(response.data, subscriptionDetails.key);
                        }
                        if (typeof this?.subscriptions?.[subscriptionDetails.key] !== 'undefined') {
                            this.subscriptions[subscriptionDetails.key].initialLoading = false;
                            this.subscriptions[subscriptionDetails.key].loading = false;
                            this.subscriptions[subscriptionDetails.key].lastRequestTime = lastRequestTime;
                        }
                    });
                }
            }
            else {
                // handle sDs calls to specific records
                let prefix = psp === 'user' ? 'ec_' : 'er_';
                if (psp === 'sch') psp = 'slot';
                let fields = params.fields;
                let recordId = params.recordId;
                let requestOptions = {
                    sDsFunction: prefix + psp + 'Field',
                    subscriptionKey: subscriptionDetails.key
                };
                this.subscriptions[subscriptionDetails.key].loading = true;
                let setHash = fields.join('');
                let uniqueSets = {};
                uniqueSets[setHash] = {
                    fields: fields,
                    recordId: recordId
                };
                if (this.subscriptions[subscriptionDetails.key].recordFields) {
                    // get only necessary fields for records
                    let tmpRecordFields = library.cloneObj(this.subscriptions[subscriptionDetails.key].recordFields);
                    uniqueSets = {};
                    for (let key in tmpRecordFields) {
                        if (tmpRecordFields.hasOwnProperty(key)) {
                            let setHash = tmpRecordFields[key].join('');
                            if (typeof uniqueSets[setHash] === 'undefined') {
                                uniqueSets[setHash] = {
                                    fields: tmpRecordFields[key],
                                    recordId: [key]
                                };
                            } else {
                                if (uniqueSets[setHash].recordId.indexOf(key) === -1) uniqueSets[setHash].recordId.push(key);
                            }
                        }
                    }
                }
                for (let i in uniqueSets) {
                    let postRequest;
                    let tmpFields = uniqueSets[i].fields;
                    let tmpRecordId = uniqueSets[i].recordId;
                    /* if (!modifiedAfter && this.records[psp][recordId]) {
                        // do not load field if it already loading in any subscription
                        let tmpFields = [];
                        for (let i in fields) {
                            if (typeof this.records[psp][recordId][fields[i]] === 'undefined' || this.records[psp][recordId][fields[i]] === null) {
                                tmpFields.push(fields[i]);
                            }
                        }
                        this.subscriptions[subscriptionDetails.key].loading = false;
                        if (!tmpFields.length) return subscriptionDetails;
                        fields = library.cloneObj(tmpFields);
                    } */

                    switch (psp) {
                        case 'user':
                        case 'vol':
                        case 'org':
                        case 'acc':
                        case 'mem':
                        case 'slot':
                        case 'sch':
                            postRequest = sDs.post(requestOptions, tmpFields, tmpRecordId, false, modifiedAfter);
                            break;
                        case 'opp':
                            postRequest = sDs.post(requestOptions, tmpFields, tmpRecordId, false, false, modifiedAfter);
                            break;
                        case 'ae':
                        case 'aff':
                            requestOptions.sDsFunction = 'sds_getProfileField';
                            // eslint-disable-next-line no-case-declarations
                            let tmpParams = { ...{ modifiedAfter: modifiedAfter }, ...params.params };
                            postRequest = sDs.post(requestOptions, psp, tmpRecordId, tmpFields, tmpParams);
                            break;
                    }
                    if (!postRequest) return false; // No Key is Produced
                    postRequest.then((response) => {
                        locals.updatePerformance(subscriptionDetails, 'serverResponseTime', performance.now() - start);
                        if (response && response.status === 'success' && response.data) {
                            this.addRecordsToSubscription(response.data, subscriptionDetails.key);
                        }
                        if (typeof this?.subscriptions?.[subscriptionDetails.key] !== 'undefined') {
                            this.subscriptions[subscriptionDetails.key].initialLoading = false;
                            this.subscriptions[subscriptionDetails.key].loading = false;
                            this.subscriptions[subscriptionDetails.key].lastRequestTime = lastRequestTime;
                        }
                    });
                }
            }
            return subscriptionDetails;
        },
        unsubscribe(existingKey, subscriber) {
            // This does not remove records from the store
            // subscriber is control name, cId of component, or other systematic ID in the code
            // providing a subscriber will remove that subscriber from one (with existingKey) or all subscriptions
            // if a subscription has no more subscribers it will be removed from the subscriptions list and no send updates
            // unsubscribe() is executed from componentMixins in unmounted() lifecycle hook
            let subscriptionKeys = [];
            // Remove a single subscription from all components, or remove one subscription from one component
            if (existingKey) subscriptionKeys.push(existingKey);
            // Allow component to remove all of it's subscriptions
            else if (subscriber) {
                let subKeys = Object.keys(this.subscriptions);
                subKeys.forEach((sKey) => {
                    if (this.subscriptions[sKey].subscribers[subscriber]) subscriptionKeys.push(sKey);
                });
            }
            subscriptionKeys.forEach((sKey) => {
                delete this.subscriptions[sKey].subscribers[subscriber];
                if (!Object.keys(this.subscriptions[sKey].subscribers).length || !subscriber) {
                    delete this.subscriptions[sKey];
                    // setting rsLastModified to 0 deletes uiTagged data
                    this.records.subscription[sKey].rsLastModified = -1;
                    delete this.records.subscription[sKey];
                }
            });
        },
        renewSubscriptions(options) {
            // Go through all subscriptions
            // Find any that need to be updated according to their on specific time
            let currentTimestamp = Math.floor(Date.now() / 1000); // NOSONAR
            let reloadIDs;
            let reloadPsp;
            if (typeof options === 'object') {
                if (typeof options.psp === 'string') reloadPsp = options.psp;
                if (Array.isArray(options.ids)) {
                    reloadIDs = library.cloneObj(options.ids);
                    for (let i in reloadIDs) {
                        reloadIDs[i] = parseInt(reloadIDs[i], 10);
                    }
                }
            }

            Object.keys(this.subscriptions).forEach((key) => {
                let subscription = this.subscriptions[key];
                let reload = false;
                if (typeof reloadPsp === 'string' && subscription.psp.toLowerCase() === reloadPsp.toLowerCase()) {
                    // reload if ids are not passed or ids are passed and some of them in subscription.recordIds
                    reload = Array.isArray(reloadIDs) ? reloadIDs.some((tmpId) => subscription.recordIds.includes(tmpId)) : true;
                }
                if (subscription.updateFrequency === 0 && !reload) return;
                if ((subscription.lastRequestTime + subscription.updateFrequency < currentTimestamp) || reload) {
                    // send the request
                    this.subscribe(false, false, true, subscription.key);
                    this.timeSinceLastRefresh = 0;
                }
            });
        },
        setModified(mRecords, forceReload) {
            // Example input
            // {
            //     opp: [12345],
            //     vol: [11111]
            // }
            let subsToUpdate = [];
            for (let psp in mRecords) {
                if (!Array.isArray(mRecords[psp])) continue;
                for (let index in mRecords[psp]) { // [11111]
                    let mRecordId = mRecords[psp][index];
                    let recordInStore = this.records[psp][mRecordId];
                    let subKeys = [];
                    if (recordInStore.inSubscription) {
                        subKeys = Object.keys(recordInStore.inSubscription);
                        subsToUpdate = [...subsToUpdate, ...subKeys];
                    }
                    if (recordInStore.uiDelete) {
                        subKeys.forEach((subscriptionKey) => {
                            let subRecords = this.subscriptions[subscriptionKey]?.recordIds;
                            if (!subRecords) return;
                            let subRecordIndex = Array.isArray(subRecords) && subRecords.indexOf(mRecordId);
                            subRecords.splice(subRecordIndex, 1);
                            delete this.records.subscription[subscriptionKey].records[mRecordId];
                        });
                        delete this.records[psp][mRecordId];
                    }
                }
            }
            subsToUpdate.forEach((subscriptionKey) => {
                this.records.subscription[subscriptionKey].rsLastModified = new Date().getTime();
                if (forceReload) this.subscribe(false, false, true, subscriptionKey);
            });
        },
        addRecordsToStore(records, recordIds, psp, subscriptionDetails) {
            if (typeof records !== 'object') return;
            let { params } = subscriptionDetails;
            let existingRecords = library.cloneObj(this.records[psp]);
            // map Ids into an object of keys for faster existing key check
            let recordIdKeys = {};
            recordIds.forEach((id) => {
                recordIdKeys[id] = true;
            });
            if (!Array.isArray(records)) {
                let recordKeys = Object.keys(records);
                let recordArr = [];
                recordKeys.forEach((rKey) => {
                    records[rKey].uiRecordKey = rKey; // Might be recordId eg: for ec_getHistory
                    recordArr.push(records[rKey]);
                });
                records = recordArr;
            }
            let deletedRecords = [];
            records.forEach((record, index) => {
                // TODO add/call formatters here
                if (!record.uiRecordId) record.uiRecordId = record.rowID || record.ID || record.id || record.VOLUNTEER_ID || record.OPPORTUNITY_ID || record.ORGANIZATION_ID || record.uiRecordKey || index;
                let recordId = record.uiRecordId;
                if (typeof recordId === 'string') recordId = Number(recordId);
                if (isNaN(recordId)) return;
                if (typeof record === 'object' && record.deleted === true) {
                    deletedRecords.push(recordId);
                    return;
                }
                // Add parent record ID to history
                if (psp === 'history') {
                    record.recordId = params.recordId;
                }
                if (psp === 'cstProperty') {
                    if (!params) return;
                    // TODO move these next few lines into the proxy?
                    if (!this.records[psp][params.cstProperty]) this.records[psp][params.cstProperty] = {};
                    if (params.subKey) {
                        if (!this.records[psp][params.cstProperty][params.subKey]) {
                            this.records[psp][params.cstProperty][params.subKey] = {};
                        }
                        this.records[psp][params.cstProperty][params.subKey][recordId] = record;
                    } else {
                        this.records[psp][params.cstProperty][recordId] = record;
                    }
                }
                else if (!existingRecords[recordId]) {
                    this.records[psp][recordId] = record;
                    this.records['strict'][psp][recordId] = record;
                } else {
                    // library.mergeObj(this.records[psp][recordId], record);
                    // we should rewrite data retrieved from server, not merge it
                    this.records[psp][recordId].doNotAutosubscribe = true;
                    this.records['strict'][psp][recordId].doNotAutosubscribe = true;
                    for (let fName in record) {
                        // handlerStore.records[psp][recordId][fName] = record[fName];
                        this.records[psp][recordId][fName] = record[fName];
                        this.records['strict'][psp][recordId][fName] = record[fName];
                    }
                    this.records[psp][recordId].doNotAutosubscribe = false;
                    this.records['strict'][psp][recordId].doNotAutosubscribe = false;
                }
                if (!recordIdKeys[recordId]) recordIds.push(recordId);
            });
            if (deletedRecords.length) {
                let tmpDeletedRecords = {};
                tmpDeletedRecords[psp] = deletedRecords;
                this.deleteRecord(tmpDeletedRecords);
            }
        },
        addRecordsToSubscription(records, subscriptionKey) {
            // Use this to put a record into the store
            // This function returns No output
            let start = performance.now();
            let subscription = this.subscriptions[subscriptionKey];
            if (!subscription) return;
            if (!records || typeof records !== 'object') {
                locals.updatePerformance(subscription, 'recordStorePopulationTime', performance.now() - start);
                return;
            }
            // Convert key/value records to array of records
            if (!Array.isArray(records)) {
                let recordKeys = Object.keys(records);
                let recordArr = [];
                recordKeys.forEach((rKey) => {
                    records[rKey].uiRecordKey = rKey; // Might be recordId eg: for ec_getHistory
                    recordArr.push(records[rKey]);
                });
                records = recordArr;
            }
            let { psp, recordIds, parentRecords, params, storeOnlyReturnedRecords } = subscription;
            if (storeOnlyReturnedRecords) recordIds = [];
            let parentVols;
            let parentOpps;
            if (parentRecords) {
                parentVols = Array.isArray(parentRecords[1]) && parentRecords[1].length && parentRecords[1];
                parentOpps = Array.isArray(parentRecords[2]) && parentRecords[2].length && parentRecords[1];
            }
            // Add the records to the store
            this.addRecordsToStore(records, recordIds, psp, subscription);
            let propInRecords = (prop) => records.length
                && typeof records[0] === 'object'
                && typeof records[0][prop] !== 'undefined';
            if (psp === 'aff') this.updateAffiliatedRecords(records);
            if (psp === 'opp' && parentVols && propInRecords('AFFILIATION_STATUS')) {
                this.updatePlaceReferRecords(records, psp, parentRecords, subscription);
            }
            if (psp === 'vol' && parentOpps && propInRecords('AFFILIATION_STATUS')) {
                this.updatePlaceReferRecords(records, psp, parentRecords, subscription);
            }
            if (psp === 'history') {
                this.updateHistoryRecords(records, subscription);
            }
            if (psp === 'sch') this.updateShiftRecords(records, subscription);
            if (psp === 'lbe') this.updateLbeRecords(records);
            // Update uiTagged Records
            let rsLastModified = new Date().getTime();
            subscription.recordIds = recordIds;
            this.records.subscription[subscriptionKey].rsLastModified = rsLastModified;
            locals.updatePerformance(subscription, 'recordStorePopulationTime', performance.now() - start);
        },
        deleteRecord(records, forceReload) {
            for (let psp in records) {
                if (psp === 'length') continue;
                records[psp].forEach((recordId) => {
                    // Flag the record for delete
                    // Records will be deleted in setModified
                    this.records[psp][recordId].uiDelete = true;
                });
            }
            this.setModified(records, forceReload);
        },
        updateHistoryRecords(records, subscription) {
            // TODO link history records to vols, opps, orgs, aff
        },
        updateAffiliatedRecords(records) {
            // TODO simplify this into a single function
            let volRecords = {};
            let orgRecords = {};
            records.forEach((record) => {
                let volId = record.VOLUNTEER_ID;
                let orgId = record.ORGANIZATION_ID;
                if (volId) {
                    let vol = volRecords[volId];
                    if (!vol) {
                        vol = { affIds: {}, affiliations: {}, VOLUNTEER_ID: volId };
                        if (record.AFF_VOLUNTEER) vol.fullName = record.AFF_VOLUNTEER;
                        volRecords[volId] = vol;
                    }
                    volRecords[volId].affIds[record.uiRecordId] = record.ORGANIZATION_ID;
                    record.linked_vol = this.records.vol[volId]; // This record gets processed through 'link' kType
                }
                if (orgId) {
                    let org = orgRecords[orgId];
                    if (!org) {
                        org = { affIds: {}, affiliations: {}, ORGANIZATION_ID: orgId };
                        if (record.AFF_ORGANIZATION) org.title = record.AFF_ORGANIZATION;
                        orgRecords[orgId] = org;
                    }
                    orgRecords[orgId].affIds[record.uiRecordId] = record.VOLUNTEER_ID;
                    record.linked_org = this.records.org[orgId]; // This record gets processed through 'link' kType
                }
            });
            locals.setRecords(volRecords, 'vol');
            locals.setRecords(orgRecords, 'org');
        },
        updatePlaceReferRecords(records, psp, parentRecords, subscription) {
            // TODO simplify this into a single function
            let volRecords = {};
            let oppRecords = {};
            records.forEach((record) => {
                let volId;
                let oppId;
                if (psp === 'vol') {
                    volId = record.VOLUNTEER_ID || record.rowID || record.ID;
                    oppId = parentRecords[2][0];
                }
                if (psp === 'opp') {
                    volId = parentRecords[1][0];
                    oppId = record.uiRecordId || record.OPPORTUNITY_ID || record.rowID || record.ID;
                }
                if (!volId || !oppId) return;
                // When setting referrals and placements, we must first reset all recordStore links inside sub records
                // Delete existing referrals and placements that match this volunteer and opportunity
                // Delete temp records
                // TODO: get rid of this by returning the refer/place ID in the sDs response
                let tempId = oppId + '00001';
                delete this.records.opp[oppId].referredIds[tempId];
                delete this.records.opp[oppId].placedIds[tempId];
                delete this.records.vol[volId].referredIds[tempId];
                delete this.records.vol[volId].placedIds[tempId];
                delete this.records.placements[tempId];
                delete this.records.referrals[tempId];
                let matchingReferrals = Object.values(this.records.opp[oppId].referrals)
                    .filter((rRecord) => typeof rRecord === 'object' && rRecord.VOL_ID === volId);
                let matchingPlacements = Object.values(this.records.opp[oppId].placements)
                    .filter((pRecord) => typeof pRecord === 'object' && pRecord.VOL_ID === volId);
                matchingReferrals.forEach((referral) => {
                    let rId = referral.uiRecordId;
                    delete this.records.opp[oppId].referredIds[rId];
                    delete this.records.vol[volId].referredIds[rId];
                    delete this.records.referrals[rId];
                });
                matchingPlacements.forEach((placement) => {
                    let pId = placement.uiRecordId;
                    delete this.records.opp[oppId].placedIds[pId];
                    delete this.records.vol[volId].placedIds[pId];
                    delete this.records.placements[pId];
                });
                let vol = volRecords[volId];
                if (!vol) {
                    vol = { placedIds: {}, referredIds: {}, placements: {}, referrals: {}, VOLUNTEER_ID: volId };
                    // Set some default values from the record
                    volRecords[volId] = vol;
                }
                let opp = oppRecords[oppId];
                if (!opp) {
                    opp = { placedIds: {}, referredIds: {}, placements: {}, referrals: {}, OPPORTUNITY_ID: oppId };
                    // Set some default values from the record
                    oppRecords[oppId] = opp;
                }
                if (record.AFFILIATION_STATUS === null) {
                    // Remove the record from the subscription if no longer affiliated
                    let recordId = record.uiRecordId || record.rowID || record.VOLUNTEER_ID || record.OPPORTUNITY_ID || record.ID;
                    let idIndex = subscription.recordIds.indexOf(recordId);
                    subscription.recordIds.splice(idIndex, 1);
                }
                else if (Array.isArray(record.AFFILIATION_STATUS)) {
                    record.AFFILIATION_STATUS.forEach((rpRecord) => {
                        rpRecord.OPP_ID = oppId;
                        rpRecord.VOL_ID = volId;
                        delete rpRecord.RECORD_ID;
                        let id = rpRecord.ID;
                        if (rpRecord.STATUS === 'referred') {
                            this.records.referrals[id] = rpRecord;
                            oppRecords[oppId].referredIds[id] = volId;
                            volRecords[volId].referredIds[id] = oppId;
                        }
                        if (rpRecord.STATUS === 'placed') {
                            this.records.placements[id] = rpRecord;
                            volRecords[volId].placedIds[id] = oppId;
                            oppRecords[oppId].placedIds[id] = volId;
                        }
                    });
                }
            });
            locals.setRecords(volRecords, 'vol');
            locals.setRecords(oppRecords, 'opp');
        },
        updateShiftRecords(records, subscription) {
            let volRecords = {};
            let oppRecords = {};
            let orgRecords = {};
            // TODO simplify this into a single function
            records.forEach((record) => {
                let volId = record.VOLUNTEER_ID || subscription?.params?.volId;
                let oppId = record.OPPORTUNITY_ID;
                let orgId = record.ORGANIZATION_ID;
                if (volId) {
                    let vol = volRecords[volId];
                    if (!vol) {
                        vol = { schIds: {}, shifts: {}, VOLUNTEER_ID: volId };
                        if (record.VOLUNTEER_NAME) vol.fullName = record.VOLUNTEER_NAME;
                        volRecords[volId] = vol;
                    }
                    volRecords[volId].schIds[record.uiRecordId] = true;
                    record.linked_vol = this.records.vol[volId]; // This record gets processed through 'link' kType
                }
                if (oppId) {
                    let opp = oppRecords[oppId];
                    if (!opp) {
                        opp = { schIds: {}, shifts: {}, OPPORTUNITY_ID: oppId };
                        if (record.OPP_TITLE) opp.OPP_TITLE = record.OPP_TITLE;
                        oppRecords[oppId] = opp;
                    }
                    oppRecords[oppId].schIds[record.uiRecordId] = true;
                    record.linked_opp = this.records.opp[oppId]; // This record gets processed through 'link' kType
                }
                if (record.ORGANIZATION_ID) {
                    let org = orgRecords[record.ORGANIZATION_ID];
                    if (!org) {
                        org = { schIds: {}, shifts: {}, ORGANIZATION_ID: orgId };
                        if (record.ORG_TITLE) org.ORG_TITLE = record.ORG_TITLE;
                        orgRecords[volId] = org;
                    }
                    orgRecords[volId].schIds[record.uiRecordId] = true;
                    record.linked_org = this.records.org[orgId]; // This record gets processed through 'link' kType
                }
            });
            locals.setRecords(volRecords, 'vol');
            locals.setRecords(oppRecords, 'opp');
            locals.setRecords(orgRecords, 'org');
        },
        updateLbeRecords(records) {
            // TODO simplify this into a single function
            // Create a copy of records.vol // opp // org
            let volRecords = {};
            let oppRecords = {};
            let orgRecords = {};
            // Build our partial vol, org, opp records using lbe data on the records
            records.forEach((record) => {
                let volId = record.VOLUNTEER_ID;
                let oppId = record.OPPORTUNITY_ID;
                let orgId = record.ORGANIZATION_ID;
                if (volId) {
                    let vol = volRecords[volId];
                    if (!vol) {
                        vol = { lbeIds: {}, lbes: {}, VOLUNTEER_ID: volId };
                        if (record.VOLUNTEER_NAME) vol.fullName = record.VOLUNTEER_NAME;
                        volRecords[volId] = vol;
                    }
                    volRecords[volId].lbeIds[record.uiRecordId] = true;
                    record.linked_vol = this.records.vol[volId]; // This record gets processed through 'link' kType
                }
                if (oppId) {
                    let opp = oppRecords[oppId];
                    if (!opp) {
                        opp = { lbeIds: {}, lbes: {}, OPPORTUNITY_ID: oppId };
                        if (record.LOGBOOK_OPP_TITLE) opp.OPP_TITLE = record.LOGBOOK_OPP_TITLE;
                        oppRecords[oppId] = opp;
                    }
                    oppRecords[oppId].lbeIds[record.uiRecordId] = true;
                    record.linked_opp = this.records.opp[oppId]; // This record gets processed through 'link' kType
                }
                if (orgId) {
                    let org = orgRecords[orgId];
                    if (!org) {
                        org = { lbeIds: {}, lbes: {}, ORGANIZATION_ID: orgId };
                        if (record.LOGBOOK_ORG_TITLE) org.ORG_TITLE = record.LOGBOOK_ORG_TITLE;
                        orgRecords[orgId] = org;
                    }
                    orgRecords[orgId].lbeIds[record.uiRecordId] = true;
                    record.linked_org = this.records.org[orgId]; // This record gets processed through 'link' kType
                }
            });
            locals.setRecords(volRecords, 'vol');
            locals.setRecords(oppRecords, 'opp');
            locals.setRecords(orgRecords, 'org');
        }
        // TODO: remove unused items from the recordStore, when?
    }
});
export { recordStore };
